import { useEffect, useRef } from 'react';
import { useWheelDragScroll } from '../../../modules/design-system';

/** How far the row fades out at an edge it can still scroll past. */
const FADE_PX = 48;

type FadeTarget = 'items' | 'parts';

function fadeMask(left: boolean, right: boolean): string {
  const start = left ? `transparent 0, #000 ${FADE_PX}px` : '#000 0';
  const end = right ? `#000 calc(100% - ${FADE_PX}px), transparent 100%` : '#000 100%';
  return `linear-gradient(to right, ${start}, ${end})`;
}

/**
 * A row wider than the page, as the site's storefront has it (`useScrollRow`):
 * its scrollbar hidden, it fades at the edges it still has items past, and a
 * mouse wheel or drag scrolls it (`useWheelDragScroll`).
 *
 * The fade goes on each item (`items`), or on each item's children (`parts`,
 * for an item whose glass is one of its parts): a mask on the row would make
 * it the backdrop root of the items, and their `backdrop-filter` would blur
 * nothing behind the row. `count` is how many items the row holds.
 */
export function useScrollRow<T extends HTMLElement>(count: number, fadeTarget: FadeTarget = 'items') {
  const ref = useRef<T>(null);
  useWheelDragScroll(ref, count);

  useEffect(() => {
    const row = ref.current;
    if (!row) return;

    const update = () => {
      const left = row.scrollLeft > 2;
      const right = row.scrollLeft + row.clientWidth < row.scrollWidth - 2;
      const mask = fadeMask(left, right);
      const size = `${row.clientWidth}px 100%`;
      const rowLeft = row.getBoundingClientRect().left;
      const items = Array.from(row.children) as HTMLElement[];
      const targets = fadeTarget === 'parts' ? items.flatMap((item) => Array.from(item.children) as HTMLElement[]) : items;
      targets.forEach((target) => {
        const position = `${rowLeft - target.getBoundingClientRect().left}px 0`;
        Object.assign(target.style, {
          maskImage: mask,
          webkitMaskImage: mask,
          maskSize: size,
          webkitMaskSize: size,
          maskPosition: position,
          webkitMaskPosition: position,
          maskRepeat: 'no-repeat',
          webkitMaskRepeat: 'no-repeat',
        });
      });
    };

    update();
    row.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(row);

    return () => {
      row.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [count, fadeTarget]);

  return ref;
}
